<script lang="ts">
  import { followLink, ui } from './host';
  // Renders a post/comment's stored media as a row of uniform SQUARE tiles, so a
  // card's text column and left edge stay identical whether or not it has media —
  // no more full-width image/video slabs that shift the layout. Images and video
  // posters share one tile treatment; multiple tiles wrap.
  //
  //   • clamped (feed/summary): a few small tiles + a "+N" overlay when there are
  //     more, so feed cards keep a consistent height.
  //   • full (detail/thread): every tile, larger.
  //   • interactive (a card that is NOT itself a click target): clicking a tile
  //     opens a lightbox — full image, inline YouTube, or (for a Skool-native or
  //     other unplayable video) the poster + a link out. On a clickable card
  //     (feed row, moderation inbox) tiles are inert previews so the card's own
  //     click wins.
  //
  // Video kinds: `skool` = a Mux stream referer-locked to skool.com (unplayable
  // off-site) → poster + a deep-link to the post; `external` = a provider embed
  // (e.g. YouTube) that can be played inline or linked out via `url`.
  import type { ImageMedia, VideoMedia } from './member';

  let {
    images = null,
    videos = null,
    clamped = false,
    side = false,
    skoolUrl = null,
    interactive = true,
  }: {
    images?: ImageMedia[] | null | undefined;
    videos?: VideoMedia[] | null | undefined;
    /** Summary mode: fewer, smaller tiles with a "+N" overflow badge. */
    clamped?: boolean;
    /** Side mode: a single square thumbnail (first item) with a "+N" badge for the
     *  rest — a fixed footprint meant to sit to the RIGHT of a card's text so the
     *  card height stays governed by the text, not the media. Click still opens the
     *  lightbox, which pages through every item. */
    side?: boolean;
    /** Deep-link target for a Skool-native video (the post on Skool). */
    skoolUrl?: string | null;
    /** When true, tiles open a lightbox; when false they're inert previews and
     *  the surrounding clickable card handles the click. */
    interactive?: boolean;
  } = $props();

  type Item = { type: 'video'; v: VideoMedia } | { type: 'image'; img: ImageMedia };

  // Videos first, then images — the order tiles render and the lightbox pages.
  const items = $derived<Item[]>([
    ...(videos ?? []).map((v): Item => ({ type: 'video', v })),
    ...(images ?? []).map((img): Item => ({ type: 'image', img })),
  ]);

  const MAX_CLAMPED = 4;
  // side → one representative tile; clamped → up to 4; full → all.
  const shown = $derived(side ? items.slice(0, 1) : clamped ? items.slice(0, MAX_CLAMPED) : items);
  const overflow = $derived(
    side ? Math.max(0, items.length - 1) : clamped ? Math.max(0, items.length - shown.length) : 0,
  );
  /**
   * ONE ATTACHMENT IS NOT A THUMBNAIL GRID.
   *
   * Every tile was a fixed square whatever the count, so a post carrying a single
   * screenshot — the common case, and the one worth reading — rendered at 80px in the
   * side panel. Unreadable, and there is nothing to grid: a row of one.
   *
   * So a lone attachment gets the column's full width and a generous height instead.
   * `side` keeps its square because that layout is a fixed thumbnail beside text by
   * definition, and two or more stay square because then it IS a grid and equal tiles
   * are what make it scannable.
   */
  const tileSize = $derived(side ? 'h-24 w-24' : clamped ? 'h-20 w-20' : 'h-28 w-28');
  // Compact chrome (small play badge, no duration chip) for the small tiles.
  const compact = $derived(clamped || side);

  function posterOf(it: Item): string | null {
    return it.type === 'image' ? it.img.url : it.v.poster_url;
  }
  function fmtDuration(ms: number | null): string | null {
    if (ms == null || ms <= 0) return null;
    const s = Math.round(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }
  // A YouTube (provider 1) external video that can be embedded inline.
  function ytEmbed(v: VideoMedia): string | null {
    return v.kind === 'external' && v.provider === 1 && v.video_id
      ? `https://www.youtube.com/embed/${v.video_id}`
      : null;
  }
  // Link target for a video that can't be played inline: the Skool post for a
  // native video, the provider URL for an external one.
  function linkOf(v: VideoMedia): string | null {
    return v.kind === 'skool' ? skoolUrl : v.url;
  }

  let lb = $state<number | null>(null);

  /**
   * Below this, the in-page viewer is not worth opening and the image goes to a real
   * browser tab instead.
   *
   * The viewer is sized in `vh`/`vw`, which is right on every surface except the one
   * that needs it most: the Helper's side panel IS the viewport. Dragged narrow, a
   * "fullscreen" screenshot is ~360px and unreadable — and no restyling reaches it,
   * because the window is the limit. Dragged wide, the very same viewer is fine.
   *
   * So the test is the SPACE AVAILABLE, not which host we are in. That is what makes
   * it track the thing the reader actually changes: widen the panel and the viewer
   * comes back, narrow it and clicks go to a tab. A host check would have to guess.
   *
   * 720px is about where a screenshot stops being legible shrunk to fit. Videos are
   * exempt — an embed needs the page it is embedded in, and a native Skool video has
   * only its post to open anyway.
   */
  const VIEWER_MIN_PX = 720;

  function open(i: number): void {
    if (!interactive) return;
    const it = items[i];
    if (it?.type === 'image' && window.innerWidth < VIEWER_MIN_PX) {
      // One click, full screen, no intermediate viewer to squint at first.
      if (!ui().openLink(it.img.url)) window.open(it.img.url, '_blank', 'noopener');
      return;
    }
    lb = i;
  }
  function close(): void {
    lb = null;
  }
  function step(d: number): void {
    if (lb == null || items.length === 0) return;
    lb = (lb + d + items.length) % items.length;
  }
  function onKey(e: KeyboardEvent): void {
    if (lb == null) return;
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowLeft') step(-1);
    else if (e.key === 'ArrowRight') step(1);
  }
</script>

<svelte:window onkeydown={onKey} />

{#snippet tileInner(it: Item, isLast: boolean)}
  {@const poster = posterOf(it)}
  {#if poster}
    <!-- `contain` for a lone attachment, `cover` for a grid. Cropping is right when
         tiles have to line up and wrong for the one image somebody opened the card to
         read — a screenshot loses its edges, which is usually where the point is. -->
    <img src={poster} alt={it.type === 'image' ? '' : (it.v.title ?? '')} loading="lazy" class="redact h-full w-full object-cover" />
  {:else}
    <div class="flex h-full w-full items-center justify-center bg-base-300/60"></div>
  {/if}
  {#if it.type === 'video'}
    {@const dur = fmtDuration(it.v.duration_ms)}
    <span class="pointer-events-none absolute inset-0 flex items-center justify-center">
      <span class="flex {compact ? 'h-8 w-8' : 'h-10 w-10'} items-center justify-center rounded-full bg-black/55 text-white ring-1 ring-white/30 backdrop-blur-sm">
        <svg class="{compact ? 'h-4 w-4' : 'h-5 w-5'} translate-x-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
      </span>
    </span>
    {#if dur && !compact}
      <span class="pointer-events-none absolute bottom-1 right-1 rounded bg-black/70 px-1 py-0.5 font-mono text-[0.6rem] font-medium text-white">{dur}</span>
    {/if}
  {/if}
  {#if isLast && overflow > 0}
    <span class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/55 text-lg font-semibold text-white">+{overflow}</span>
  {/if}
{/snippet}

{#if items.length}
  <div class="flex flex-wrap gap-2">
    {#each shown as it, i (i)}
      {#if interactive}
        <button
          type="button"
          class="relative {tileSize} shrink-0 overflow-hidden rounded-box border hair transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          onclick={() => open(i)}
          aria-label={it.type === 'image' ? 'View image' : 'Play video'}
        >
          {@render tileInner(it, i === shown.length - 1)}
        </button>
      {:else}
        <div class="relative {tileSize} shrink-0 overflow-hidden rounded-box border hair">
          {@render tileInner(it, i === shown.length - 1)}
        </div>
      {/if}
    {/each}
  </div>
{/if}

{#if lb != null}
  {@const it = items[lb]}
  {#if it}
    <!-- Lightbox overlay. z-[100] to clear the app chrome; backdrop click + Esc close. -->
    <div class="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-2 sm:p-8" role="dialog" aria-modal="true" aria-label="Media viewer">
      <button type="button" class="absolute inset-0 cursor-default" aria-label="Close viewer" onclick={close}></button>

      <div class="pointer-events-none relative z-10 flex max-h-full max-w-full items-center justify-center">
        {#if it.type === 'image'}
          <img src={it.img.url} alt="" class="pointer-events-auto max-h-[88vh] max-w-full rounded-lg object-contain shadow-2xl" />
        {:else}
          {@const yt = ytEmbed(it.v)}
          {#if yt}
            <div class="pointer-events-auto aspect-video w-[min(92vw,1000px)] overflow-hidden rounded-lg shadow-2xl">
              <iframe
                src={yt}
                title={it.v.title ?? 'Video'}
                class="h-full w-full"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                referrerpolicy="strict-origin-when-cross-origin"
              ></iframe>
            </div>
          {:else}
            {@const href = linkOf(it.v)}
            <div class="pointer-events-auto flex max-w-[92vw] flex-col items-center gap-4">
              {#if it.v.poster_url}
                <img src={it.v.poster_url} alt="" class="max-h-[70vh] max-w-full rounded-lg object-contain shadow-2xl" />
              {/if}
              {#if href}
                <a href={href} target="_blank" rel="noopener noreferrer" onclick={(e) => followLink(e, href)} class="btn btn-primary btn-sm gap-2">
                  <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                  {it.v.kind === 'skool' ? 'Watch on Skool' : 'Open video'}
                </a>
              {/if}
            </div>
          {/if}
        {/if}
      </div>

      <!--
        THE WAY OUT OF A 400px VIEWPORT.

        This viewer is sized in `vh`/`vw`, which is the right unit everywhere except
        the one surface that needs it most: the Helper's side panel IS the viewport, so
        "fullscreen" there is ~368px wide and a screenshot in a post is unreadable. No
        amount of restyling fixes that — the window is the limit.

        So the answer is to leave. `openLink` already knows how each host does that: the
        panel opens a real browser tab (`openFromContent`), and the dashboard falls
        through to the anchor's own `target="_blank"`. Shown on images only — a video
        already has its own "Watch on Skool" for the same reason.

        Top-LEFT because close is top-right and the two must not be confused: one of
        them throws the viewer away.
      -->
      {#if it.type === 'image'}
        <a
          href={it.img.url}
          target="_blank"
          rel="noopener noreferrer"
          onclick={(e) => followLink(e, it.img.url)}
          class="absolute left-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-white/20"
          title="Open the full-size image in a browser tab"
        >
          <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7" /><polyline points="7 7 17 7 17 17" /></svg>
          Full size
        </a>
      {/if}

      <button type="button" class="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20" aria-label="Close" onclick={close}>
        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>

      {#if items.length > 1}
        <button type="button" class="absolute left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20" aria-label="Previous" onclick={() => step(-1)}>
          <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <button type="button" class="absolute right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20" aria-label="Next" onclick={() => step(1)}>
          <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6" /></svg>
        </button>
        <span class="absolute bottom-3 left-1/2 z-20 -translate-x-1/2 rounded-full bg-black/60 px-2.5 py-1 font-mono text-xs text-white">{lb + 1} / {items.length}</span>
      {/if}
    </div>
  {/if}
{/if}
